import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {
	Box,
	Flex,
	ListView,
	PrimaryButton,
	StatusTag,
	CheckCircleIcon,
	ExternalBankIcon,
	FormikCheckbox,
} from '@stashinvest/ui';
import { BanjoString } from '@stashinvest/react-banjo';
import { Text as LegacyText } from '@stashinvest/ui/dist/es/2';
import CalendarDay from 'stash/components/calendar-day/CalendarDay';
import Disclosure from '../../global/Disclosure';
import { Form, Formik } from 'formik';
import { diffArray } from 'stash/utils';
import { PlanDetailsCard } from '../../subscription/PlanDetailsCard';
import { Text } from '@stashinvest/react-components';
import useTrack from 'stash/hooks/useTrack';
import {
	v1_RecurringTransactionAuthorization_Viewed,
	v1_RecurringTransactionAuthorization_Submitted,
} from '@stashinvest/product-events-javascript/lib/v1/event_static_methods';
import { v1_RecurringTransactionAuthorization_ViewedProperties } from '@stashinvest/product-events-javascript/lib/v1/properties';
import { Screen } from '@stashinvest/product-events-javascript/lib/enum_properties';

const BankTransactionsIcon = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 64px;
	height: 64px;
	margin: 0 auto;
	background-color: ${({ theme }) => theme.colors.bgSecondary};
	border: 2px solid ${({ theme }) => theme.colors.borderDefault};
	border-radius: ${({ theme }) => theme.borderRadius.radius8};
`;

const BankTransactionsCheck = styled(CheckCircleIcon)`
	position: absolute;
	top: -12px;
	right: -12px;
`;

const ReauthConfirm = ({
	bankLinkType,
	bankName,
	reauthorizationData = {},
	billingSummary,
	linkSelectedAccount,
	maxButtonWidth,
}) => {
	const track = useTrack();
	const screen = reauthorizationData.authorization?.screen || {};
	const transfers = reauthorizationData.authorization?.transfers || [];
	const destinationTypes = transfers.map((transfer) => transfer.destinationType);
	const baseAnalytics = {
		external_account_type:
			v1_RecurringTransactionAuthorization_ViewedProperties.ExternalAccountType?.bank,
		external_account_connection_type:
			bankLinkType === 'micros'
				? v1_RecurringTransactionAuthorization_ViewedProperties
						.ExternalAccountConnectionType?.microDeposits
				: bankLinkType === 'plaid'
				? v1_RecurringTransactionAuthorization_ViewedProperties
						.ExternalAccountConnectionType?.instant
				: null,
	};
	useEffect(() => {
		track(v1_RecurringTransactionAuthorization_Viewed(baseAnalytics));
	}, []);

	return (
		<Box>
			<BankTransactionsIcon>
				<ExternalBankIcon size="40px" />
				<BankTransactionsCheck color="bgPositivePrimary" size="24px" />
			</BankTransactionsIcon>
			<LegacyText.Title28 align="left" mb="s12" mt="s24" fontWeight="bold">
				{screen.title || 'Confirm recurring transactions for your linked bank'}
			</LegacyText.Title28>
			<LegacyText.Body16 align="left">
				<strong>{bankName || 'This account'}</strong> will be used for the following
				auto-transactions.
			</LegacyText.Body16>
			{billingSummary && (
				<PlanDetailsCard className="my-contentSmall" billingSummary={billingSummary} />
			)}
			<Formik
				initialValues={{ checked: destinationTypes }}
				onSubmit={(values, actions) => {
					const checked = ['SUBSCRIPTION', ...values.checked];
					const unchecked = diffArray(destinationTypes, checked);
					track(
						v1_RecurringTransactionAuthorization_Submitted({
							...baseAnalytics,
							screen: Screen.recurringTransactionAuthorization,
							authorized: checked.join(' '),
							not_authorized: unchecked.join(' '),
						})
					);

					const requestBody = {
						authorization: {
							transfers: transfers.map((transfer) => {
								return {
									destinationType: transfer.destinationType,
									destinationAccountId: transfer.destinationAccountId,
									authorized: checked.includes(transfer.destinationType),
								};
							}),
						},
					};

					linkSelectedAccount(requestBody).then(() => {
						actions.setSubmitting(false);
					});
				}}
			>
				{(formikProps) => (
					<Form>
						<ul>
							{transfers.map((account, key) => (
								<Box key={key}>
									<ListView
										m="none"
										p="none"
										pt="xs"
										img={
											<CalendarDay
												status={account.status}
												date={account.nextExecutionDate}
											/>
										}
										title={
											<LegacyText.Body14 fontWeight="bold">
												{account.title}
											</LegacyText.Body14>
										}
										subtitle={
											<LegacyText.Body14>{account.description}</LegacyText.Body14>
										}
										value={
											<Flex
												flexDirection={{ desktop: 'row' }}
												justifyContent="space-between"
												alignItems="center"
											>
												<Box>
													<StatusTag
														variant={account.status === 'paused' ? 'pending' : 'on'}
													>
														{account.status}
													</StatusTag>
												</Box>
												<Box ml="xs" alignItems={'right'}>
													<FormikCheckbox
														name="checked"
														value={account.destinationType}
														mr="0"
													/>
												</Box>
											</Flex>
										}
									/>
								</Box>
							))}
						</ul>
						<Box mt="s">
							{!billingSummary && (
								<Text className="my-contentSmall" textStyle="captionSmallStandard">
									<BanjoString stringId="information/disclose/bankSubAuthFallback" />
								</Text>
							)}
							<Text className="my-contentSmall" textStyle="captionSmallStandard">
								<Disclosure
									copy={[
										<BanjoString
											stringId="information/disclose/recurringTransactionsAuthorization"
											key={'information/disclose/recurringTransactionsAuthorization'}
										/>,
										<BanjoString
											stringId="information/disclose/genericBankMention"
											key={'information/disclose/genericBankMention'}
										/>,
									]}
								/>
							</Text>
							<Box maxWidth={maxButtonWidth} mx="auto">
								<PrimaryButton loading={formikProps.isSubmitting} type="submit">
									{screen.ctaTitle || 'Confirm'}
								</PrimaryButton>
							</Box>
						</Box>
					</Form>
				)}
			</Formik>
		</Box>
	);
};

ReauthConfirm.propTypes = {
	bankLinkType: PropTypes.oneOf(['plaid', 'micros']).isRequired,
	reauthorizationData: PropTypes.object,
	accountName: PropTypes.string,
	maskedNumber: PropTypes.string,
	bankName: PropTypes.string.isRequired,
	linkSelectedAccount: PropTypes.func.isRequired,
	billingSummary: PropTypes.object,
};

export const ReauthConfirmBankWrapper = ({
	bankLinkType,
	reauthorizationData,
	billingSummary,
	accountName = '',
	maskedNumber = '',
	...props
}) => {
	const bankName = {
		plaid: `${accountName} ${maskedNumber}`,
		micros: reauthorizationData?.authorization?.screen?.bankName,
	};

	const userReadableBankName = bankName[bankLinkType];

	return (
		<ReauthConfirm
			bankLinkType={bankLinkType}
			bankName={userReadableBankName}
			reauthorizationData={reauthorizationData}
			billingSummary={billingSummary}
			{...props}
		/>
	);
};

ReauthConfirmBankWrapper.propTypes = {
	bankLinkType: PropTypes.oneOf(['plaid', 'micros']).isRequired,
	reauthorizationData: PropTypes.object.isRequired,
	accountName: PropTypes.string,
	maskedNumber: PropTypes.string,
	billingSummary: PropTypes.object,
};

export default ReauthConfirm;
